import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { BookOpen, ArrowRight, Menu, X, Moon, Sun } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

export const NAV_PRODUCT_ITEMS = [
  { label: "Features", to: "/features" },
  { label: "How it Works", to: "/how-it-works" },
  { label: "Shelves", to: "/shelves" },
  { label: "Circles", to: "/circles" },
];

export const NAV_ITEMS = [
  ...NAV_PRODUCT_ITEMS,
  { label: "Reading Guides", to: "/reading-guides" },
  { label: "Blog", to: "/book-blog" },
  { label: "Contact Us", to: "/help-center" },
];

const ThemeToggle = () => {
  const { theme, toggle } = useTheme();
  return (
    <button
      onClick={toggle}
      data-testid="theme-toggle-btn"
      aria-label="Toggle theme"
      className="relative w-10 h-10 rounded-full flex items-center justify-center border hover:scale-105 transition-transform"
      style={{
        background: "var(--surface-2)",
        borderColor: "var(--border)",
        color: "var(--text)",
      }}
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={theme}
          initial={{ rotate: -90, opacity: 0, scale: 0.6 }}
          animate={{ rotate: 0, opacity: 1, scale: 1 }}
          exit={{ rotate: 90, opacity: 0, scale: 0.6 }}
          transition={{ duration: 0.25 }}
          className="flex items-center justify-center"
        >
          {theme === "light" ? (
            <Moon className="w-4 h-4" strokeWidth={2} />
          ) : (
            <Sun className="w-4 h-4" strokeWidth={2} />
          )}
        </motion.span>
      </AnimatePresence>
    </button>
  );
};

const navLinkClass = (active) =>
  `px-2.5 xl:px-3.5 py-2 text-[13px] xl:text-sm whitespace-nowrap transition-colors duration-300 rounded-full ${
    active ? "font-medium" : ""
  }`;

const SiteNavbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  const isActive = (to) => pathname === to;
  const baseLinkStyle = { color: "var(--text-soft)" };

  const navLinkHandlers = (active) => ({
    onMouseEnter: (e) => {
      if (!active) e.currentTarget.style.color = "var(--text)";
    },
    onMouseLeave: (e) => {
      if (!active) e.currentTarget.style.color = "var(--text-soft)";
    },
  });

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "glass py-3" : "py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between">
        <Link
          to="/"
          data-testid="navbar-logo"
          className="flex items-center gap-2.5 group"
        >
          <div
            className="relative w-10 h-10 rounded-xl flex items-center justify-center"
            style={{
              background:
                "linear-gradient(135deg, var(--brand) 0%, var(--accent) 100%)",
              boxShadow: "0 4px 16px -4px rgba(194,90,55,0.35)",
            }}
          >
            <BookOpen
              className="w-4.5 h-4.5"
              style={{ color: "var(--on-brand)" }}
              strokeWidth={2.3}
            />
          </div>
          <span
            className="font-serif text-xl font-semibold tracking-tight"
            style={{ color: "var(--text)" }}
          >
            PagePeers
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-0.5 xl:gap-1 flex-1 justify-center max-w-3xl mx-4">
          {NAV_ITEMS.map((item) => {
            const active = isActive(item.to);
            return (
              <Link
                key={item.label}
                to={item.to}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                className={navLinkClass(active)}
                style={{
                  ...baseLinkStyle,
                  color: active ? "var(--text)" : baseLinkStyle.color,
                }}
                {...navLinkHandlers(active)}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <ThemeToggle />
          <a
            href="https://club.pagepeers.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-login-btn"
            className="hidden md:inline-block text-sm px-3 py-2 transition-colors"
            style={{ color: "var(--text-soft)" }}
          >
            Login
          </a>
          <a
            href="https://club.pagepeers.com/register"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-join-btn"
            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 hover:scale-[1.02]"
            style={{
              background: "var(--brand)",
              color: "var(--on-brand)",
              boxShadow: "0 6px 20px -6px rgba(194,90,55,0.5)",
            }}
          >
            Join the Club
            <ArrowRight className="w-4 h-4" />
          </a>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setMobileOpen((s) => !s)}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full border"
            style={{ borderColor: "var(--border)", color: "var(--text)" }}
          >
            {mobileOpen ? (
              <X className="w-5 h-5" />
            ) : (
              <Menu className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden glass overflow-hidden"
          >
            <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-2">
              {NAV_ITEMS.map((item) => {
                const active = isActive(item.to);
                return (
                  <Link
                    key={item.label}
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    className="px-4 py-3 rounded-xl"
                    style={{
                      color: active ? "var(--brand)" : "var(--text)",
                    }}
                  >
                    {item.label}
                  </Link>
                );
              })}
              <a
                href="https://club.pagepeers.com/register"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-medium"
                style={{ background: "var(--brand)", color: "var(--on-brand)" }}
              >
                Join the Club <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default SiteNavbar;
